import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Hand,
  Ear,
  Coffee,
  HeartHandshake,
  Phone,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp, stagger } from "@/lib/motion";

const SITE_URL = "https://psyloveyourself.com";
const PAGE_URL = `${SITE_URL}/krisi-panikou`;

export default function KrisiPanikou() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Σύντομος οδηγός: Όταν σε βρίσκει κρίση άγχους ή πανικού",
    description:
      "Πρακτικός, σύντομος οδηγός για το τι να κάνεις τη στιγμή που νιώθεις κρίση άγχους ή πανικού. Τεχνικές αναπνοής, grounding 5-4-3-2-1, και ηρεμιστικά μηνύματα.",
    inLanguage: "el-GR",
    author: { "@type": "Person", name: "Άννα Παπαχρονόπουλου" },
    publisher: { "@type": "Organization", name: "psyloveyourself" },
    mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL },
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{"Κρίση άγχους & πανικού — Σύντομος οδηγός | psyloveyourself"}</title>
        <meta
          name="description"
          content="Αν σε βρίσκει κρίση άγχους ή πανικού αυτή τη στιγμή — διάβασε αυτό. Τεχνικές αναπνοής, grounding 5-4-3-2-1 και ηρεμιστικά μηνύματα από ψυχολόγο στην Πάτρα."
        />
        <meta
          name="keywords"
          content="κρίση πανικού, κρίση άγχους, τι να κάνω σε κρίση πανικού, αναπνοή 4-7-8, grounding 5-4-3-2-1, panic attack ελληνικά"
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Κρίση άγχους & πανικού — Σύντομος οδηγός" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Header />

      <main
        className="bg-background pt-32 pb-24 relative overflow-hidden"
        data-testid="krisi-page"
      >
        {/* Soft warm wash */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-[60%] h-[60%] bg-primary/10 blur-[140px] rounded-full" />
          <div className="absolute bottom-0 left-0 w-[50%] h-[60%] bg-secondary/30 blur-[140px] rounded-full" />
        </div>

        <div className="max-w-[820px] mx-auto px-6 sm:px-12">
          {/* Reassuring intro */}
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>

            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5">
              Σύντομος οδηγός
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]">
              Αν διαβάζεις αυτό <span className="italic font-light">τώρα,</span>
              <br />
              ανάπνευσε. Είσαι ασφαλής.
            </h1>
            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed">
              Οι κρίσεις άγχους και πανικού είναι έντονες — αλλά σύντομες, μη
              επικίνδυνες, και πάντα <strong className="text-foreground font-normal">περνούν</strong>. Ό,τι νιώθεις δεν
              σημαίνει ότι «κάτι κακό συμβαίνει». Σημαίνει ότι το νευρικό σου
              σύστημα ενεργοποιήθηκε υπερβολικά — και μπορεί να ηρεμήσει.
            </p>
          </motion.div>

          {/* Step 1 — Breathing */}
          <motion.section
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-16"
          >
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-heading italic text-primary text-2xl">01</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
                Ανάπνευσε <span className="italic font-light">4 — 7 — 8.</span>
              </h2>
            </div>

            <div className="bg-card rounded-3xl border border-border/50 p-8 sm:p-12 flex flex-col items-center text-center">
              {/* Breathing animated circle */}
              <div
                className="relative w-44 h-44 sm:w-56 sm:h-56 mb-8"
                data-testid="breathing-circle"
              >
                <div className="absolute inset-0 rounded-full bg-primary/15 animate-[breathe_19s_ease-in-out_infinite]" />
                <div className="absolute inset-3 rounded-full bg-primary/20 animate-[breathe_19s_ease-in-out_infinite]" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-heading italic text-foreground/80 text-xl">
                    ανάπνευσε
                  </span>
                </div>
              </div>

              <p className="font-body font-light text-foreground/80 text-base sm:text-lg leading-relaxed max-w-[420px]">
                Εισπνοή από τη <strong className="text-foreground font-normal">μύτη</strong> για 4 δευτερόλεπτα.
                Κράτα την αναπνοή για <strong className="text-foreground font-normal">7</strong>. Εκπνοή αργά από το <strong className="text-foreground font-normal">στόμα</strong> για 8.
              </p>
              <p className="mt-3 font-body text-sm text-muted-foreground italic">
                Επανάλαβε 4 φορές. Το σώμα ηρεμεί φυσιολογικά μέσα σε λίγα λεπτά.
              </p>
            </div>
          </motion.section>

          {/* Step 2 — Grounding */}
          <motion.section
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-12"
          >
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-heading italic text-primary text-2xl">02</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
                Επίστρεψε <span className="italic font-light">στο σώμα.</span>
              </h2>
            </div>
            <p className="font-body font-light text-foreground/75 leading-relaxed mb-7">
              Η τεχνική <strong className="text-foreground font-normal">5–4–3–2–1</strong> «γειώνει» τον εγκέφαλο
              στο εδώ και τώρα, βγάζοντάς τον από τη φυγή/πάλη. Γύρω σου, βρες:
            </p>

            <motion.ul {...stagger(0.07)} className="space-y-3">
              {[
                {
                  n: 5,
                  icon: Eye,
                  text: "πράγματα που βλέπεις (ένα φυτό, μια μπογιά στον τοίχο, ένα σύννεφο)",
                },
                {
                  n: 4,
                  icon: Hand,
                  text: "πράγματα που μπορείς να αγγίξεις (το ύφασμα της μπλούζας, το πάτωμα)",
                },
                { n: 3, icon: Ear, text: "ήχους που ακούς (αυτοκίνητο, ανεμιστήρας, αναπνοή)" },
                {
                  n: 2,
                  icon: Coffee,
                  text: "πράγματα που μυρίζεις ή θυμάσαι το άρωμά τους",
                },
                { n: 1, icon: HeartHandshake, text: "πράγμα που νιώθεις για τον εαυτό σου με αγάπη" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <motion.li
                    key={s.n}
                    variants={{
                      initial: { opacity: 0, x: -16 },
                      whileInView: {
                        opacity: 1,
                        x: 0,
                        transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
                      },
                    }}
                    className="flex items-start gap-4 bg-background border border-border/60 rounded-2xl p-5 sm:p-6"
                  >
                    <span className="font-heading italic text-3xl text-primary leading-none w-10 shrink-0">
                      {s.n}
                    </span>
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-card flex items-center justify-center text-foreground/70">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>
                    <span className="font-body font-light text-foreground/85 leading-relaxed pt-1">
                      {s.text}
                    </span>
                  </motion.li>
                );
              })}
            </motion.ul>
          </motion.section>

          {/* Step 3 — Reminders */}
          <motion.section
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.35 }}
            className="mt-12"
          >
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-heading italic text-primary text-2xl">03</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
                Πες μέσα σου <span className="italic font-light">αυτές τις φράσεις.</span>
              </h2>
            </div>
            <div className="space-y-3">
              {[
                "«Αυτό είναι μια κρίση πανικού. Δεν θα μου κάνει κακό.»",
                "«Το σώμα μου είναι σε υπερβολή — δεν είναι σε κίνδυνο.»",
                "«Έχω περάσει από αυτό πριν, και πέρασε. Θα περάσει ξανά.»",
                "«Δεν χρειάζεται να κάνω τίποτα. Απλώς ν' αφήσω το κύμα να φύγει.»",
                "«Είμαι ασφαλής. Είμαι ασφαλής. Είμαι ασφαλής.»",
              ].map((q, i) => (
                <motion.blockquote
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="font-heading italic text-foreground/85 text-xl sm:text-2xl leading-snug pl-5 border-l-2 border-primary/60"
                >
                  {q}
                </motion.blockquote>
              ))}
            </div>
          </motion.section>

          {/* Sos panel */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.45 }}
            className="mt-16 p-8 sm:p-10 bg-foreground text-background rounded-3xl"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-background/70 mb-4">
              Αν χρειάζεσαι κάποιον να μιλήσεις
            </p>
            <h3 className="font-heading font-medium text-3xl sm:text-4xl tracking-tight leading-[1.1]">
              Δεν είσαι <span className="italic font-light">μόνος/η.</span>
            </h3>
            <p className="mt-4 font-body font-light text-background/85 leading-relaxed max-w-[520px]">
              Αν οι κρίσεις επαναλαμβάνονται ή σε φοβίζουν, η ψυχοθεραπεία (CBT)
              είναι από τις πιο αποτελεσματικές παρεμβάσεις. Μπορούμε να
              συνεργαστούμε.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4">
              <Link
                to="/#contact"
                data-testid="krisi-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all"
              >
                Κλείσε συνεδρία
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background/30 px-7 py-3.5 font-body text-sm font-medium text-background hover:bg-background/10 transition-all"
              >
                <Phone size={14} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>

            <p className="mt-8 pt-6 border-t border-background/15 text-xs font-body text-background/60 leading-relaxed">
              Σε περίπτωση επείγουσας ανάγκης ή σκέψεων αυτοτραυματισμού:
              κάλεσε <strong className="text-background">166</strong> (ΕΚΑΒ) ή τη Γραμμή Ψυχολογικής
              Υποστήριξης <strong className="text-background">10306</strong> (Υπουργείο Υγείας — δωρεάν, 24/7).
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
